import React, { forwardRef, useEffect, useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';

// Every top-nav dropdown renders through this: portalled into <body> and
// position: fixed at z-[100], so nothing on the page can paint over it.
// Rendered inside the sticky header, iOS Safari composited the panels on the
// header's layer and they flickered behind scrolling tables below it.
//
// Placement follows the anchor's box: `align` right/left pins that edge,
// `matchWidth` copies the anchor's width, `stretchOnPhone` spans the viewport
// (8px gutters) below 640px. Height caps at the viewport (or `maxHeight`)
// and scrolls, so long menus stay reachable in landscape.
//
// Callers doing outside-click detection must also treat the panel (via the
// forwarded ref) as "inside" — it no longer lives under the anchor in the DOM;
// `pressedOutside` does that. `onDismiss` runs when the backdrop is tapped.
const GUTTER = 8;

// Touch devices and narrow windows get a modal menu: the page behind can't be
// tapped or dragged. Desktop mice keep click-through dropdowns.
const MODAL_MENU_MEDIA = '(pointer: coarse), (max-width: 1023px)';

// Ref-counted so overlapping menus (one closing as the next opens) share it.
// overflow:hidden alone doesn't stop every iOS drag, so touchmove is cancelled
// too — unless it starts inside an opted-in menu that can actually scroll.
// Only <body> is locked: App.css gives body `overflow-x: hidden`, so any
// overflow on <html> stops body's overflow reaching the viewport and makes body
// its own unscrolled box; the sticky header then snaps to the page top, off-screen
// when scrolled down. (position:fixed on <body> would do the same.)
let lockCount = 0;
let restoreLock = null;

function scrollsWithinMenu(target) {
  const root = target instanceof Element ? target.closest('[data-scroll-lock-allow]') : null;
  if (!root) return false;
  for (let el = target; el; el = el.parentElement) {
    const oy = getComputedStyle(el).overflowY;
    if ((oy === 'auto' || oy === 'scroll') && el.scrollHeight > el.clientHeight) return true;
    if (el === root) return false;
  }
  return false;
}

function lockPageScroll() {
  if (lockCount++ === 0) {
    const html = document.documentElement;
    const { body } = document;
    const gutter = window.innerWidth - html.clientWidth;
    const prev = { bodyOverflow: body.style.overflow, bodyPad: body.style.paddingRight };
    const onTouchMove = (e) => { if (!scrollsWithinMenu(e.target)) e.preventDefault(); };
    body.style.overflow = 'hidden';
    if (gutter > 0) body.style.paddingRight = `${gutter}px`;
    document.addEventListener('touchmove', onTouchMove, { passive: false });
    restoreLock = () => {
      body.style.overflow = prev.bodyOverflow;
      body.style.paddingRight = prev.bodyPad;
      document.removeEventListener('touchmove', onTouchMove);
    };
  }
  return () => {
    lockCount -= 1;
    if (lockCount === 0 && restoreLock) { restoreLock(); restoreLock = null; }
  };
}

// Invisible layer between the page and the header (header is z-40) while a
// menu is open. A tap on it closes the menu and nothing else: it closes on
// click, not mousedown — closing on mousedown would unmount the layer and let
// the tap's click land on the page underneath.
export function MenuBackdrop({ onDismiss }) {
  const [active] = useState(() => !!window.matchMedia?.(MODAL_MENU_MEDIA).matches);
  useEffect(() => (active ? lockPageScroll() : undefined), [active]);
  if (!active) return null;
  return createPortal(
    <div
      className="fixed inset-0 z-[39]"
      style={{ touchAction: 'none' }}
      aria-hidden="true"
      data-menu-backdrop=""
      data-testid="menu-backdrop"
      onClick={onDismiss}
    />,
    document.body,
  );
}

// For document mousedown handlers: true when the press is outside every ref.
// Presses on the backdrop don't count — the backdrop's own click closes.
export function pressedOutside(e, refs) {
  if (e.target instanceof Element && e.target.closest('[data-menu-backdrop]')) return false;
  return !refs.some((r) => r.current?.contains(e.target));
}

const TopNavPanel = forwardRef(function TopNavPanel(
  { anchorRef, onDismiss, align = 'right', matchWidth = false, stretchOnPhone = false, gap = 4, maxHeight = Infinity, className = '', style, children, ...rest },
  ref,
) {
  const [box, setBox] = useState(null);

  useLayoutEffect(() => {
    const place = () => {
      const r = anchorRef.current?.getBoundingClientRect();
      if (!r) return;
      const vw = document.documentElement.clientWidth;
      const top = r.bottom + gap;
      const next = { top, maxHeight: Math.min(maxHeight, window.innerHeight - top - GUTTER) };
      if (stretchOnPhone && vw < 640) {
        next.left = GUTTER;
        next.right = GUTTER;
      } else if (matchWidth) {
        next.left = r.left;
        next.width = r.width;
      } else if (align === 'left') {
        next.left = Math.max(GUTTER, r.left);
      } else {
        next.right = Math.max(GUTTER, vw - r.right);
      }
      setBox(next);
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [anchorRef, align, matchWidth, stretchOnPhone, gap, maxHeight]);

  if (!box) return null;
  return (
    <>
      <MenuBackdrop onDismiss={onDismiss} />
      {createPortal(
        <div
          ref={ref}
          className={`fixed z-[100] overflow-y-auto overscroll-contain ${className}`}
          style={{ ...box, ...style }}
          data-scroll-lock-allow=""
          {...rest}
        >
          {children}
        </div>,
        document.body,
      )}
    </>
  );
});

export default TopNavPanel;
